import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

interface EmailTemplate {
  subject: string;
  content: string;
}

const emailTemplates = {
  welcome: (name: string): EmailTemplate => ({
    subject: 'Welcome to Location Scout!',
    content: `
      <div style="text-align: center;">
        <h1 style="color: #2563eb; margin-bottom: 20px;">Welcome to Location Scout!</h1>
        <p style="font-size: 16px;">Hi ${name},</p>
        <p style="font-size: 16px;">Thank you for joining Location Scout. We're excited to help you find the perfect location for your business!</p>
        <div style="margin: 30px 0;">
          <a href="${window.location.origin}/search" style="background-color: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">Start Your Search</a>
        </div>
      </div>
    `
  }),
  locationAlert: (location: string, score: number): EmailTemplate => ({
    subject: 'New Location Match Found!',
    content: `
      <div style="text-align: center;">
        <h1 style="color: #2563eb; margin-bottom: 20px;">New Location Match!</h1>
        <p style="font-size: 16px;">We found a new location that matches your criteria:</p>
        <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h2 style="color: #1e40af;">${location}</h2>
          <p style="font-size: 18px;">Match Score: ${score}%</p>
        </div>
        <div style="margin: 30px 0;">
          <a href="${window.location.origin}/results" style="background-color: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">View Details</a>
        </div>
      </div>
    `
  }),
  weeklyDigest: (data: { insights: string[] }): EmailTemplate => ({
    subject: 'Your Weekly Location Scout Digest',
    content: `
      <div style="text-align: center;">
        <h1 style="color: #2563eb; margin-bottom: 20px;">Weekly Market Insights</h1>
        <div style="text-align: left; margin: 20px 0;">
          ${data.insights.map(insight => `
            <div style="background-color: #f8fafc; padding: 15px; border-radius: 6px; margin-bottom: 10px;">
              ${insight}
            </div>
          `).join('')}
        </div>
        <div style="margin: 30px 0;">
          <a href="${window.location.origin}/analytics" style="background-color: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">View Full Analytics</a>
        </div>
      </div>
    `
  })
};

export async function sendEmail(to: string, subject: string, content: string): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No active session');
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        subject,
        content,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to send email');
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to send email');
    }

    return data;
  } catch (error: any) {
    console.error('Email sending error:', error);
    throw new Error(error.message || 'Failed to send email');
  }
}

export async function sendTemplatedEmail(
  to: string,
  templateName: keyof typeof emailTemplates,
  templateData: any
): Promise<void> {
  try {
    const template = emailTemplates[templateName];
    if (!template) {
      throw new Error(`Template "${templateName}" not found`);
    }

    const { subject, content } = template(templateData);
    await sendEmail(to, subject, content);
  } catch (error: any) {
    console.error('Template email error:', error);
    throw new Error(error.message || 'Failed to send templated email');
  }
}

export async function sendNotification(userId: string, type: string, message: string): Promise<void> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('User not found');

    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (profileError) throw profileError;
    if (!profile?.notification_preferences?.email) return;

    await sendEmail(
      user.email!,
      `Location Scout - ${type}`,
      message
    );
  } catch (error: any) {
    console.error('Notification error:', error);
    throw new Error(error.message || 'Failed to send notification');
  }
}

export async function scheduleWeeklyDigest(userId: string): Promise<void> {
  try {
    const insights = [
      'Market growth in downtown area increased by 15%',
      'New development projects announced in tech district',
      'Retail foot traffic showing strong recovery',
    ];

    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('User not found');

    await sendTemplatedEmail(user.email!, 'weeklyDigest', { insights });
  } catch (error: any) {
    console.error('Weekly digest error:', error);
    throw new Error(error.message || 'Failed to send weekly digest');
  }
}